import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import toast, { Toaster } from 'react-hot-toast';
import { showConfirm, showError } from '../lib/swal';
import BotonCargando from '../components/BotonCargando';

// Lista de permisos que se pueden asignar, con su nombre legible y
// una explicación de qué habilita cada uno.
const PERMISOS = [
  {
    clave: 'aprobar_registros',
    titulo: 'Aprobar registros',
    descripcion: 'Puede aprobar o rechazar los registros de cambio de tóner',
  },
  {
    clave: 'aprobar_envios',
    titulo: 'Aprobar envíos a clientes',
    descripcion: 'Puede aprobar o rechazar los envíos de tóner a clientes',
  },
  {
    clave: 'gestionar_clientes',
    titulo: 'Gestionar clientes',
    descripcion: 'Puede crear, editar y eliminar clientes',
  },
  {
    clave: 'gestionar_maquinas',
    titulo: 'Gestionar máquinas',
    descripcion: 'Accede a la sección Máquinas',
  },
  {
    clave: 'gestionar_toners',
    titulo: 'Gestionar tóners',
    descripcion: 'Accede a la sección Tóners',
  },
  {
    clave: 'ver_historial',
    titulo: 'Ver historial de envíos',
    descripcion: 'Accede a la sección Historial de Envíos',
  },
];

export default function Usuarios() {
  const { user } = useAuthStore();

  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardandoId, setGuardandoId] = useState(null);

  // Modal de creación
  const [modalCrear, setModalCrear] = useState(false);
  const [nuevoEmail, setNuevoEmail] = useState('');
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [nuevoRol, setNuevoRol] = useState('secretaria');
  const [creando, setCreando] = useState(false);

  const cargarUsuarios = async () => {
    setCargando(true);
    const { data, error } = await supabase.rpc('listar_usuarios');
    if (error) {
      console.error('Error al listar usuarios:', error);
      showError('No se pudo cargar la lista de usuarios', error.message);
      setUsuarios([]);
    } else {
      setUsuarios(data || []);
    }
    setCargando(false);
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  // Cambiar un permiso en la vista (todavía sin guardar)
  const alternarPermiso = (usuarioId, clave) => {
    setUsuarios((prev) =>
      prev.map((u) =>
        u.id === usuarioId ? { ...u, [clave]: !u[clave], _modificado: true } : u
      )
    );
  };

  const cambiarRol = (usuarioId, rol) => {
    setUsuarios((prev) =>
      prev.map((u) => (u.id === usuarioId ? { ...u, rol, _modificado: true } : u))
    );
  };

  const guardarCambios = async (u) => {
    setGuardandoId(u.id);
    const { data, error } = await supabase.rpc('actualizar_permisos', {
      p_usuario_id: u.id,
      p_rol: u.rol,
      p_aprobar_registros: u.aprobar_registros,
      p_aprobar_envios: u.aprobar_envios,
      p_gestionar_clientes: u.gestionar_clientes,
      p_gestionar_maquinas: u.gestionar_maquinas,
      p_gestionar_toners: u.gestionar_toners,
      p_ver_historial: u.ver_historial,
    });

    if (error) {
      showError('No se pudieron guardar los cambios', error.message);
    } else {
      toast.success(data?.mensaje || 'Cambios guardados');
      setUsuarios((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, _modificado: false } : x))
      );
    }
    setGuardandoId(null);
  };

  const crearUsuario = async () => {
    if (!nuevoEmail.trim() || !nuevaPassword.trim()) {
      showError('Faltan datos', 'Ingresa el correo y la contraseña.');
      return;
    }
    if (nuevaPassword.length < 6) {
      showError('Contraseña muy corta', 'Debe tener al menos 6 caracteres.');
      return;
    }

    setCreando(true);
    const { data, error } = await supabase.rpc('crear_usuario', {
      p_email: nuevoEmail.trim(),
      p_password: nuevaPassword,
      p_rol: nuevoRol,
    });

    if (error) {
      showError('No se pudo crear el usuario', error.message);
    } else {
      toast.success(data?.mensaje || 'Usuario creado');
      setModalCrear(false);
      setNuevoEmail('');
      setNuevaPassword('');
      setNuevoRol('secretaria');
      cargarUsuarios();
    }
    setCreando(false);
  };

  const eliminarUsuario = async (u) => {
    const confirmado = await showConfirm(
      '¿Eliminar este usuario?',
      `Se eliminará la cuenta de ${u.email}. Esta acción no se puede deshacer.`,
      { peligro: true }
    );
    if (!confirmado) return;

    const { data, error } = await supabase.rpc('eliminar_usuario', {
      p_usuario_id: u.id,
    });

    if (error) {
      showError('No se pudo eliminar el usuario', error.message);
    } else {
      toast.success(data?.mensaje || 'Usuario eliminado');
      cargarUsuarios();
    }
  };

  return (
    <div className="ml-0 lg:ml-72 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-6 min-h-screen animate-fadeIn">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Gestión de Usuarios</span>
            </h1>
            <p className="text-gray-600">
              {usuarios.length} {usuarios.length === 1 ? 'usuario' : 'usuarios'} registrados
            </p>
          </div>
          <button
            onClick={() => setModalCrear(true)}
            className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Aviso sobre los administradores */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <span className="text-xl">ℹ️</span>
        <p className="text-sm text-blue-900">
          Los usuarios con rol <strong>Administrador</strong> tienen acceso completo al sistema
          de forma automática. Los permisos de abajo solo aplican a las <strong>secretarias</strong>,
          para darles accesos adicionales.
        </p>
      </div>

      {cargando ? (
        <div className="bg-white shadow-xl rounded-2xl p-12 text-center border border-gray-100">
          <svg className="animate-spin w-10 h-10 mx-auto text-blue-600 mb-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-600">Cargando usuarios...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {usuarios.map((u) => {
            const esAdmin = u.rol === 'admin';
            const esYo = u.id === user?.id;

            return (
              <div
                key={u.id}
                className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 border border-gray-100"
              >
                {/* Cabecera del usuario */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 break-all">{u.email}</h3>
                      {esYo && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                          Tú
                        </span>
                      )}
                      {!u.confirmado && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                          Sin confirmar
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Selector de rol */}
                    <select
                      value={u.rol}
                      onChange={(e) => cambiarRol(u.id, e.target.value)}
                      className={`px-3 py-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                        esAdmin
                          ? 'border-purple-300 bg-purple-50 text-purple-800'
                          : 'border-gray-200 bg-white text-gray-800'
                      }`}
                    >
                      <option value="secretaria">Secretaria</option>
                      <option value="admin">Administrador</option>
                    </select>

                    {!esYo && (
                      <button
                        onClick={() => eliminarUsuario(u)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all duration-200"
                        title="Eliminar usuario"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>

                {/* Permisos */}
                {esAdmin ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-900">
                    👑 Como administrador, tiene acceso completo a todas las secciones.
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Permisos adicionales
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {PERMISOS.map((p) => (
                        <label
                          key={p.clave}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            u[p.clave]
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!u[p.clave]}
                            onChange={() => alternarPermiso(u.id, p.clave)}
                            className="mt-0.5 w-5 h-5 rounded accent-green-600 flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-gray-800">{p.titulo}</div>
                            <div className="text-xs text-gray-500">{p.descripcion}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {/* Guardar */}
                {u._modificado && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-sm text-amber-700">Hay cambios sin guardar</span>
                    <BotonCargando
                      cargando={guardandoId === u.id}
                      textoCargando="Guardando..."
                      onClick={() => guardarCambios(u)}
                      className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-xl hover:shadow-lg"
                    >
                      Guardar cambios
                    </BotonCargando>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: crear usuario */}
      {modalCrear && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="text-lg font-bold">Nuevo Usuario</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={nuevoEmail}
                  onChange={(e) => setNuevoEmail(e.target.value)}
                  placeholder="usuario@empresa.cl"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contraseña <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevaPassword}
                  onChange={(e) => setNuevaPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Anótala y entrégasela a la persona. Podrá cambiarla después.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rol</label>
                <select
                  value={nuevoRol}
                  onChange={(e) => setNuevoRol(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                >
                  <option value="secretaria">Secretaria</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3 rounded-b-2xl">
              <BotonCargando
                cargando={creando}
                textoCargando="Creando..."
                onClick={crearUsuario}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg"
              >
                Crear Usuario
              </BotonCargando>
              <button
                onClick={() => setModalCrear(false)}
                disabled={creando}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-all duration-200 disabled:opacity-60"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
